"use client";

import { noop } from "@zetalabs/core";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { DownloadSessionManager } from "../core/download/download";
import { Scopes, SubjectHash } from "../core/hash";
import {
	FileDownload,
	FileUpload,
	InitialSessionData,
	Session,
	SessionType,
} from "../core/types";
import { UploadSessionManager } from "../core/upload/upload";
import { useFileManager } from "./use-file-manager";

// TODO: (bwilytsch): Accept array of files in upload
type UseSessionReturn<T extends SessionType> = T extends SessionType.Upload
	? {
			session: Session<FileUpload> | null;
			upload: (file: File) => FileUpload;
			delete: (id: string) => Promise<void>;
		}
	: {
			session: Session<FileDownload> | null;
			download: (uri: string) => FileDownload;
		};

export function useSession<T extends SessionType>(
	subject: string | undefined | null,
	type: T,
	scopes?: Scopes,
	data?: InitialSessionData<T>,
	onChange: (session: Session<any>) => void = noop,
	softDelete?: boolean,
): UseSessionReturn<T> {
	const manager = useFileManager();
	const sessionManager = useRef<
		UploadSessionManager | DownloadSessionManager | null
	>(null);
	const [session, setSession] = useState<Session<any> | null>(null);
	const hash = useMemo(
		() => (subject ? new SubjectHash([subject, ...(scopes ?? [])]) : null),
		[subject, scopes?.length],
	);

	useEffect(() => {
		// NOTE: (bwilytsch): I might need to compare existing session data with initialData
		if (!hash || sessionManager.current) {
			return;
		}

		const sm = manager.createSession<T>(hash, type, data);
		// NOTE: (bwilytsch): Maybe an observable would be better here
		const unsubscribe = sm.on("change", (updatedSession) => {
			setSession(updatedSession);
			onChange(updatedSession);
		});

		sessionManager.current = sm;
		setSession(sm.session);

		return () => {
			unsubscribe();
			sessionManager.current = null;
		};
	}, [subject, scopes, type, manager, hash, data]);

	const upload = useCallback(
		(file: File) => {
			if (sessionManager.current instanceof UploadSessionManager) {
				return sessionManager.current.upload(file);
			} else {
				return undefined;
			}
		},
		[subject, manager, type, scopes],
	);

	const download = useCallback(
		(uri: string) => {
			if (sessionManager.current instanceof DownloadSessionManager) {
				return sessionManager.current.download(uri);
			} else {
				return undefined;
			}
		},
		[subject, manager, type, scopes],
	);

	if (type === SessionType.Download) {
		return {
			session,
			download,
		} as UseSessionReturn<T>;
	}

	if (type === SessionType.Upload) {
		return {
			session,
			upload,
			delete: async (id: string) => {
				return sessionManager.current?.delete(id, softDelete);
			},
		} as UseSessionReturn<T>;
	}

	throw new Error(`Unknown session type ${type}`);
}

export function useUploadSession(
	subject: string | undefined | null,
	scopes?: Scopes,
) {
	return useSession(subject, SessionType.Upload, scopes);
}

export function useDownloadSession(
	subject: string | undefined | null,
	scopes?: Scopes,
) {
	return useSession(subject, SessionType.Upload, scopes);
}
